// Multi-tenancy: um front, vários backends (ver SPEC_MULTITENANCY.md).
//
// O tenant ativo é resolvido uma única vez, na carga da página, e não muda até
// a página ser recarregada. Trocar de cliente = recarregar (switchTenant).
// A sessão (token, nome, userId) fica em chaves próprias de cada cliente, para
// um token nunca ser enviado ao backend de outro cliente.

export const SLUG_RE = /^[a-z0-9][a-z0-9-]{0,29}$/;
const URL_RE = /^https?:\/\/[A-Za-z0-9._:-]+(\/[A-Za-z0-9._~/-]*)?$/;

const ACTIVE_KEY = "ptf:tenant";
const DEFAULT_SLUG = "default";
const LEGACY_KEYS = ["token", "name", "userId"];

export const sessionKey = (slug, field) => `ptf:${slug}:${field}`;

const readEnv = () => (typeof window !== "undefined" && window._env_) || {};

// Normaliza e valida o registro. Entradas inválidas são descartadas.
// Sem TENANTS, o BACK_URL (runtime ou build) vira o cliente único "default".
export function getTenants(env = readEnv(), buildUrl = process.env.REACT_APP_BACK_URL) {
  const seen = new Set();
  const tenants = [];

  if (Array.isArray(env.TENANTS)) {
    env.TENANTS.forEach((t) => {
      if (!t || !SLUG_RE.test(t.slug) || seen.has(t.slug) || !URL_RE.test(t.url || "")) {
        return;
      }
      seen.add(t.slug);
      tenants.push({
        slug: t.slug,
        name: String(t.name || t.slug),
        url: t.url.replace(/\/+$/, ""),
        api: t.api || "",
      });
    });
  }

  if (tenants.length === 0) {
    const url = env.BACK_URL || buildUrl;
    if (url) {
      tenants.push({ slug: DEFAULT_SLUG, name: "", url: url.replace(/\/+$/, ""), api: "" });
    }
  }

  return tenants;
}

// Ordem: ?tenant=<slug> > cliente desta aba > último usado (qualquer aba) >
// único tenant existente > nenhum.
// O cliente da aba (sessionStorage) faz um F5 manter o cliente mesmo que outra
// aba tenha escolhido outro; o "último usado" (localStorage) só vale para abas novas.
// A URL do backend só sai do registro; a query string só escolhe entre os
// cadastrados.
export function resolveActiveTenant(
  tenants,
  search = "",
  storage = localStorage,
  tabStorage = sessionStorage
) {
  const bySlug = (slug) => tenants.find((t) => t.slug === slug) || null;

  const fromQuery = new URLSearchParams(search).get("tenant");
  if (fromQuery && SLUG_RE.test(fromQuery) && bySlug(fromQuery)) {
    return bySlug(fromQuery);
  }

  const ofTab = bySlug(tabStorage.getItem(ACTIVE_KEY));
  if (ofTab) return ofTab;

  const stored = bySlug(storage.getItem(ACTIVE_KEY));
  if (stored) return stored;

  return tenants.length === 1 ? tenants[0] : null;
}

// ---- tenant ativo (singleton resolvido na primeira leitura) ----

let cached;

function removeLegacySession() {
  // Chaves antigas (sem prefixo) não têm dono conhecido: descartar.
  try {
    LEGACY_KEYS.forEach((k) => localStorage.removeItem(k));
  } catch (e) {
    /* storage indisponível */
  }
}

function rememberTenant(slug) {
  localStorage.setItem(ACTIVE_KEY, slug);
  sessionStorage.setItem(ACTIVE_KEY, slug);
}

export function getActiveTenant() {
  if (cached !== undefined) return cached.active;

  removeLegacySession();
  const tenants = getTenants();
  let active = null;
  try {
    active = resolveActiveTenant(tenants, window.location.search);
    if (active) rememberTenant(active.slug);
  } catch (e) {
    active = tenants.length === 1 ? tenants[0] : null;
  }
  cached = { tenants, active };
  return active;
}

export function listTenants() {
  getActiveTenant();
  return cached.tenants;
}

// Só para testes.
export function __resetTenantCache() {
  cached = undefined;
}

// Troca de cliente: grava a escolha e recarrega o app em `path`. Sem sessão no
// novo cliente, o PrivateRoute leva ao login.
export function switchTenant(slug, path = "/logs_pathfind") {
  if (!SLUG_RE.test(slug) || !listTenants().some((t) => t.slug === slug)) return;
  rememberTenant(slug);
  window.location.assign(path);
}

// ---- sessão por cliente ----

export function getSession(slug) {
  if (!slug) return { token: null, name: null, userId: null };
  return {
    token: localStorage.getItem(sessionKey(slug, "token")),
    name: localStorage.getItem(sessionKey(slug, "name")),
    userId: localStorage.getItem(sessionKey(slug, "userId")),
  };
}

export function setSession(slug, { token, name, userId }) {
  localStorage.setItem(sessionKey(slug, "token"), token);
  localStorage.setItem(sessionKey(slug, "name"), name ?? "");
  localStorage.setItem(sessionKey(slug, "userId"), userId ?? "");
}

export function clearSession(slug) {
  if (!slug) return;
  ["token", "name", "userId"].forEach((f) => localStorage.removeItem(sessionKey(slug, f)));
}

export function hasSession(slug) {
  return !!getSession(slug).token;
}
